import { firestore } from "firebase";
import { v4 as uuid } from "uuid";
import sort from "fast-sort";
import _ from "lodash";
import dayjs from "dayjs";

import { excludeByKeys, formatDateToDate } from "src/helpers/UtilityHelper";

import DatabaseAdapter from "src/firebase/db/DatabaseAdapter";
import {
  ReferenceError,
  InternalError,
} from "src/firebase/db/ApplicationError";
import OrderBreadsDB, { OrderBreadEntity } from "src/firebase/db/OrderBreads";
import { OrderItemEntity } from "src/firebase/db/OrderItems";
import { OrderOptionEntity } from "src/firebase/db/OrderOptions";
import { ShopBagEntity } from "src/firebase/db/ShopBags";
import NotificationsDB, {
  NotificationEntity,
} from "src/firebase/db/Notifications";

export type OrderItemProps = OrderItemEntity & {
  order_bread: OrderBreadEntity;
  id: string;
};
export type OrderOptionProps = OrderOptionEntity & {
  bag: ShopBagEntity;
  id: string;
};

export const orderInitialEntity = {
  id: "",
  amount: 0,
  bread_id: "",
  created_at: {} as firestore.Timestamp,
  fee_amount: 0,
  fee_rate: 0,
  image: "",
  items: [] as OrderItemProps[],
  options: [] as OrderOptionProps[],
  last_pickup_time: {} as firestore.Timestamp,
  name: "",
  notifications_ref: {} as firestore.DocumentReference,
  order_breads_ref: {} as firestore.DocumentReference,
  order_number: "",
  payment_intent: "",
  payment_method: "",
  pickup_supplement: "",
  price: 0,
  quantity: 0,
  receipt_url: "",
  shop_address: "",
  shop_id: "",
  shop_name: "",
  status: "",
  is_confirmed: false,
  is_completed: false,
  updated_at: {} as firestore.Timestamp,
  user_id: "",
  user_request_at_pickup: "",
};
export type OrderEntity = typeof orderInitialEntity;
export const orderEntityKeys = (Object.keys(
  orderInitialEntity
) as unknown) as (keyof OrderEntity)[];

export const orderRevenueIntialEntity = {
  totalAmount: 0,
  totalCount: 0,
  item: [] as OrderEntity[],
};

export type OrderRevenueEntity = typeof orderRevenueIntialEntity;

class OrdersDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    const keys = excludeByKeys(orderEntityKeys, ["order_items"]);
    this.setSchemaKeys(keys);
  }

  // 旧式のデータ取得（明細が存在しない時のメソッド）
  getOrders = async (orderBreadId: string) => {
    const orderBreadsRef = this.getConnection("order_breads").doc(orderBreadId);
    const docRef = this.getConnection()
      .orderBy("created_at", "asc")
      .where("status", "==", "succeeded")
      .where("order_breads_ref", "==", orderBreadsRef);
    return docRef
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError("注文が存在しません");
        }
        const orders = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            return {
              ...data,
            } as OrderEntity;
          })
        );
        return this.formatSuccess<OrderEntity[]>(orders);
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  };

  // 単品取得：ほぼonSnapshot用
  getOrder = async (id: string) => {
    return this.getDocumentRef(id)
      .get()
      .then(async (doc) => {
        if (!doc.exists) {
          throw new ReferenceError("注文が存在しません");
        }
        const data = (await doc.data()) as OrderEntity;
        const items = await this.getItems(doc.ref);
        const options = await this.getOptions(doc.ref);
        const order = {
          ...data,
          id: doc.id,
          items,
          options,
        } as OrderEntity;
        return this.formatSuccess<OrderEntity>(order);
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  };

  // 確認済に更新
  async updateIsConfirmed(id: string, isConfirmed: boolean) {
    return this.getDocumentRef(id)
      .update({
        is_confirmed: isConfirmed,
        updated_at: this.getServerTimeStamp(),
      })
      .then(() => {
        return this.formatSuccess({
          message: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 受渡済に更新
  async updateIsCompleted(id: string, isCompleted: boolean) {
    return this.getDocumentRef(id)
      .update({
        is_completed: isCompleted,
        updated_at: this.getServerTimeStamp(),
      })
      .then(() => {
        return this.formatSuccess({
          message: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 期間指定の注文データを取得
  async getDayOrders(
    shopId: string,
    startDate: Date,
    endDate: Date,
    target: "created_at" | "last_pickup_time", // フィルター対象
    filter?: "normal" | "reservation", // 全て or 予約通知のみか
    noDetails?: boolean // 明細も取得するか否か。読み込みが重いので不必要なデータを読みたくない時にtrueにする
  ) {
    let orders = [] as OrderEntity[];

    return await this.getConnection()
      .where("shop_id", "==", shopId)
      .where(target, ">=", startDate)
      .where(target, "<=", endDate)
      .where("status", "==", "succeeded")
      .get()
      .then(async (snapshot) => {
        // 通知が存在しない場合
        if (snapshot.empty) {
          throw new ReferenceError("注文がありません。");
        }

        // 取得した通知に詳細情報を付与する
        await Promise.all(
          snapshot.docs.map(async (orderDoc) => {
            const order = (await orderDoc.data()) as OrderEntity;

            if (noDetails) {
              orders.push({
                ...order,
                id: orderDoc.id,
                items: [],
                options: [],
              } as OrderEntity);
            } else {
              const items = await this.getItems(orderDoc.ref);
              const options = await this.getOptions(orderDoc.ref);

              orders.push({
                ...order,
                id: orderDoc.id,
                items,
                options,
              } as OrderEntity);
            }
          })
        );

        // 商品種別でフィルタリング
        if (filter) {
          orders = orders.filter(({ created_at: a, last_pickup_time: b }) => {
            const d = dayjs(formatDateToDate(a));
            const isSameDay = d.isSame(formatDateToDate(b), "day");
            // 通常商品の場合はデータ作成日とパン受取日が同じ、予約はその逆
            return filter === "normal" ? isSameDay : !isSameDay;
          });
        }

        // Firestoreのソートが効いてないので修正
        const results = sort(orders).by([
          { desc: (v) => formatDateToDate(v.created_at).getTime() },
        ]);

        return this.formatSuccess<OrderEntity[]>(results);
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // まとめて通知に紐づく注文データを取得
  getNotificationOrders = async (
    shopId: string,
    notificationsRef: firebase.firestore.DocumentReference
  ) => {
    const docRef = await this.getConnection()
      .orderBy("created_at", "asc")
      .where("shop_id", "==", shopId)
      .where("status", "==", "succeeded")
      .where("notifications_ref", "==", notificationsRef);
    return docRef
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError("注文が存在しません");
        }
        const orders = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = (await doc.data()) as OrderEntity;

            // 紐づく明細データをセット
            let items = [] as OrderItemEntity[];
            try {
              const itemsQuerySnapShot = await doc.ref
                .collection("items")
                .get();
              items = await Promise.all(
                itemsQuerySnapShot.docs.map(
                  async (itemSnapShot) =>
                    (await itemSnapShot.data()) as OrderItemEntity
                )
              );
            } catch (e) {
              // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
              items = [];
              console.error(e);
            }

            // 紐づく明細データをセット
            let options = [] as OrderOptionEntity[];
            try {
              const optionsQuerySnapShot = await doc.ref
                .collection("options")
                .get();
              options = await Promise.all(
                optionsQuerySnapShot.docs.map(
                  async (optionSnapShot) =>
                    (await optionSnapShot.data()) as OrderOptionEntity
                )
              );
            } catch (e) {
              // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
              options = [];
              console.error(e);
            }

            return {
              ...data,
              id: doc.id,
              items,
              options,
            } as OrderEntity;
          })
        );
        return this.formatSuccess<OrderEntity[]>(orders);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  };

  // 各ユーザーに紐づくデータを取得
  getUsersOrders = async (user_ids: string[]) => {
    // in演算子は10件を超えるとクラッシュするので、分割して取得する
    const chunks = _.chunk(user_ids, 10);

    const records = await Promise.all(
      chunks.map(
        async (chunk) =>
          await this.getConnection()
            .where("user_id", "in", chunk)
            .get()
            .then((snapshot) =>
              Promise.all(
                snapshot.docs.map(
                  async (doc) => (await doc.data()) as OrderEntity
                )
              )
            )
            .catch((err) => {
              console.error(err);
              if (err instanceof ReferenceError) {
                throw err;
              }
              throw new InternalError(err.message);
            })
      )
    );

    const orders = _(records).flatten().value();
    const sorted = sort(orders).by([
      { desc: (v) => formatDateToDate(v.created_at).getTime() },
    ]);
    return this.formatSuccess<OrderEntity[]>(sorted);
  };

  getMonthRevenue = async ({
    shopId,
    startDate,
    endDate,
  }: {
    shopId: string;
    startDate: Date;
    endDate: Date;
  }) => {
    const docRef = this.getConnection()
      .where("shop_id", "==", shopId)
      .orderBy("created_at", "asc")
      .startAt(startDate)
      .endAt(endDate);
    let response = { ...orderRevenueIntialEntity };
    return docRef
      .get()
      .then((snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError("指定された範囲に注文が存在しません");
        } else {
          snapshot.forEach((doc) => {
            const amount = (doc.get("amount") - doc.get("fee_amount")) | 0;
            const quantity = doc.get("quantity") | 0;
            response["totalAmount"] += amount;
            response["totalCount"] += quantity;
            response["item"].push(doc.data() as OrderEntity);
          });
          return this.formatSuccess<OrderRevenueEntity>(response);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  };

  // Deprecated!! デバッグ用のダミーデータを作成。
  async create(notificationId: string) {
    // const fixedUid = "vnXLzmdMbLfeMZWUOmZ5O0shJs53";
    const fixedUid = "thisisdummyuid";

    const notificationRef = await NotificationsDB.getDocumentRef(
      notificationId
    );
    const notification = await notificationRef
      .get()
      .then(async (notificationSnapShot) => {
        return notificationSnapShot.data() as NotificationEntity;
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });

    const orderBreadCollectionRef = OrderBreadsDB.getConnection();
    const orderBreads = await orderBreadCollectionRef
      .where("notifications_ref", "==", notificationRef)
      .get()
      .then(async (orderBreadSnapShot) => {
        if (orderBreadSnapShot.empty) {
          throw new ReferenceError("通知に紐づくパンが存在しません。");
        }
        return await Promise.all(
          orderBreadSnapShot.docs.map(
            async (doc) => doc.data() as OrderBreadEntity
          )
        );
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });

    const prices = orderBreads.map(
      (orderBread) => orderBread.price
    ) as number[];
    const amount = _.sum(prices);

    const requestQuery = {
      ...orderInitialEntity,
      amount,
      created_at: this.getServerTimeStamp(),
      fee_amount: Math.round(amount * 0.05),
      fee_rate: 0.05,
      notifications_ref: notificationRef,
      order_number: uuid().slice(0, 6).toUpperCase(),
      payment_intent: "pi_dummypaymentintent",
      payment_method: "pm_dummypaymentmethod",
      pickup_supplement: "ダミーデータ",
      receipt_url: "http://dev-sacri-admin.web.app/",
      shop_address: notification.shop_address,
      shop_id: notification.shop_id,
      shop_name: notification.shop_name,
      status: "succeeded",
      updated_at: this.getServerTimeStamp(),
      user_id: fixedUid,
    };

    const batch = OrdersDB.startTransaction();

    // Orderを作成（合計データ）
    await this.getConnection()
      .add(requestQuery)
      .then(async (orderDocRef) => {
        const { id: orderId } = orderDocRef;

        // 更新後のデータ取得
        await orderDocRef.update({ id: orderId });

        // ダミーの明細データ作成
        const items: OrderItemEntity[] = orderBreads.map((orderBread) => ({
          name: orderBread.name,
          image: orderBread.image,
          order_breads_ref: orderBreadCollectionRef.doc(),
          order_id: orderId,
          price: orderBread.price,
          quantity: 1,
        }));

        await Promise.all(
          items.map(async (item) => {
            await orderDocRef.collection("items").add(item);
          })
        );

        return orderDocRef;
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });

    OrdersDB.endTransaction(batch);
  }

  // 明細（パン）を取得
  async getItems(ref: firebase.firestore.DocumentReference) {
    const snapshot = await ref.collection("items").get();
    const items: OrderItemEntity[] = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const data = doc.data() as OrderItemEntity;
        const orderBread = (await (
          await data.order_breads_ref.get()
        ).data()) as OrderBreadEntity;
        return {
          ...data,
          id: doc.id,
          order_bread: orderBread,
        };
      })
    );
    return sort(items).by([{ asc: ({ name }) => name }]);
  }

  // 明細（レジ袋など）を取得
  async getOptions(ref: firebase.firestore.DocumentReference) {
    const snapshot = await ref.collection("options").get();
    const options: OrderOptionEntity[] = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const data = doc.data() as OrderOptionEntity;
        const bag = (await (await data.ref.get()).data()) as ShopBagEntity;
        return {
          ...data,
          id: doc.id,
          bag: bag,
        };
      })
    );
    return sort(options).by([{ desc: ({ price }) => price }]);
  }
}

export default new OrdersDB({
  schema: "orders",
});
